/**
 * Tiny client-side thread summarizer — picks the first user turn as the
 * working title for a RAELA thread (capped to ~48 chars). Runs on the
 * client to avoid an extra round-trip; persists once via the existing
 * `raela_threads.title` column when the title is empty.
 */
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Msg {
  role: string;
  content: string;
}

const summarize = (msgs: Msg[]): string | null => {
  const firstUser = msgs.find((m) => m.role === "user")?.content?.trim();
  if (!firstUser) return null;
  const oneLine = firstUser.replace(/\s+/g, " ").trim();
  if (oneLine.length <= 48) return oneLine;
  return oneLine.slice(0, 45).trimEnd() + "…";
};

export function useThreadAutoSummary(
  threadId: string | null,
  messages: Msg[],
  currentTitle: string | null,
) {
  useEffect(() => {
    if (!threadId || currentTitle) return;
    if (messages.length < 2) return; // wait until we have at least one exchange
    const next = summarize(messages);
    if (!next) return;
    void supabase
      .from("raela_threads")
      .update({ title: next })
      .eq("id", threadId)
      .is("title", null);
  }, [threadId, messages, currentTitle]);
}
